import { type FC } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import {
  CHART_AXIS_STROKE,
  CHART_COLOR_PALETTE,
  CHART_GRID_STROKE,
  CHART_TICK_COLOR,
  CHART_TOOLTIP_CURSOR,
} from "src/components/charts/chartPalette";
import { mirrorChartMargin, rtlChartProps } from "src/components/charts/chartRtl";
import RestaurantSectionCard from "../RestaurantSectionCard";
import { useRestaurantFormatters } from "../../utils/restaurantFormatters";

export interface CategoryBarDatum {
  id: string;
  label: string;
  value: number;
}

interface RestaurantCategoryBarsProps {
  title: string;
  tip?: string;
  data: CategoryBarDatum[];
  valueKind?: "currency" | "number";
  colorIndex?: number;
}

const RestaurantCategoryBars: FC<RestaurantCategoryBarsProps> = ({
  title,
  tip,
  data,
  valueKind = "currency",
  colorIndex = 0,
}) => {
  const fmt = useRestaurantFormatters();
  const formatValue = (value: number) =>
    valueKind === "currency" ? fmt.formatCurrency(value, { compact: true }) : fmt.formatNumber(value);
  const chartRtl = rtlChartProps(fmt.isRTL);
  const maxValue = Math.max(0, ...data.map(item => item.value));
  const longestTick = formatValue(maxValue).length;
  const yAxisWidth = Math.max(44, longestTick * 7 + 12);
  const longestLabel = data.reduce((max, item) => Math.max(max, item.label.length), 0);
  const xAxisHeight = Math.min(110, Math.max(44, longestLabel * 4.2 + 24));
  // Ticks are rotated with `textAnchor="end"`, so each label trails back from its own
  // tick: the first category's text runs past the leading plot edge and the card clips
  // it (the "D" of "Delivery fee too high"). Reserve room on that side proportional to
  // the longest label, the mirror of the trailing room `right` already gives the last one.
  const firstLabelLength = data[0]?.label.length ?? 0;
  const leadingOverhang = Math.min(72, Math.round(Math.min(firstLabelLength, longestLabel) * 2.6));

  return (
    <RestaurantSectionCard title={title} tip={tip}>
      <div className="restaurant-chart restaurant-chart--category-bars">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            // Tick labels are rotated with `textAnchor="end"`, so each one extends back
            // from its tick — the final category needs side room or it gets clipped.
            margin={mirrorChartMargin(
              { top: 8, right: 24, left: leadingOverhang, bottom: 8 },
              fmt.isRTL,
            )}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
            <XAxis
              dataKey="label"
              interval={0}
              height={xAxisHeight}
              angle={-18}
              textAnchor="end"
              reversed={chartRtl.axisReversed}
              tick={{ fontSize: 11, fill: CHART_TICK_COLOR }}
              axisLine={{ stroke: CHART_AXIS_STROKE }}
              tickLine={false}
            />
            <YAxis
              orientation={chartRtl.yAxisOrientation}
              width={yAxisWidth}
              tick={{ fontSize: 11, fill: CHART_TICK_COLOR }}
              axisLine={{ stroke: CHART_AXIS_STROKE }}
              tickLine={false}
              tickFormatter={value => formatValue(Number(value))}
            />
            <Tooltip
              content={<CustomChartTooltip formatTooltip={value => formatValue(value)} hideSeriesName />}
              cursor={{ fill: CHART_TOOLTIP_CURSOR }}
              wrapperStyle={{ pointerEvents: "none" }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={38}>
              {data.map((item, index) => (
                <Cell
                  key={item.id}
                  fill={CHART_COLOR_PALETTE[(index + colorIndex) % CHART_COLOR_PALETTE.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </RestaurantSectionCard>
  );
};

export default RestaurantCategoryBars;
